import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { Timer } from 'three/addons/misc/Timer.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import CANNON from 'cannon'
import GUI from 'lil-gui'

/**
 * Base
 */
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
scene.background = new THREE.Color('#050409')
scene.fog = new THREE.Fog('rgba(121, 117, 135, 1)', 1, 60)
const gui = new GUI()
const targetAspect = 16 / 9
const letterboxMinPx = 40
const letterboxFrac = 0.12
const letterboxTop = document.createElement('div')
const letterboxBottom = document.createElement('div')
const letterboxElements = [letterboxTop, letterboxBottom]
letterboxElements.forEach((bar) =>
{
    bar.style.position = 'fixed'
    bar.style.left = '0'
    bar.style.width = '100%'
    bar.style.height = '0px'
    bar.style.background = 'rgba(0, 0, 0, 1)'
    bar.style.pointerEvents = 'none'
    bar.style.zIndex = '5'
    bar.style.transition = 'height 0.6s ease'
    document.body.appendChild(bar)
})
letterboxTop.style.top = '0'
letterboxBottom.style.bottom = '0'
let letterboxVisible = false

const setLetterbox = (visible) =>
{
    letterboxVisible = visible
    if(!visible)
    {
        letterboxTop.style.height = '0px'
        letterboxBottom.style.height = '0px'
        return
    }

    const computed = (window.innerHeight - window.innerWidth / targetAspect) * 0.5
    const fallback = Math.max(letterboxMinPx, window.innerHeight * letterboxFrac)
    const targetHeight = computed > 0 ? computed : fallback
    const heightPx = `${targetHeight}px`
    letterboxTop.style.height = heightPx
    letterboxBottom.style.height = heightPx
}

// Music control (place audio file at /audio/bluevelvet.mp3)
const music = new Audio('/audio/bluevelvet.mp3')
music.loop = true
music.volume = 0.6
const musicParams = {
    play: async () =>
    {
        animationPlaying = true
        setLetterbox(true)
        autoCamera.enabled = true
        controls.enabled = false
        autoCamera.startTime = null
        if(modelAction)
        {
            modelAction.reset()
            modelAction.paused = false
            modelAction.play()
        }
        try
        {
            await music.play()
        }
        catch(error)
        {
            console.warn('Music play failed:', error)
        }
    },
    pause: () =>
    {
        animationPlaying = false
        setLetterbox(false)
        autoCamera.enabled = cameraParams.cinematic
        controls.enabled = !autoCamera.enabled
        if(modelAction)
        {
            modelAction.paused = true
        }
        music.pause()
    },
    volume: music.volume
}
const musicFolder = gui.addFolder('Music')
musicFolder.add(musicParams, 'play').name('Play')
musicFolder.add(musicParams, 'pause').name('Pause')
musicFolder.add(musicParams, 'volume').min(0).max(1).step(0.01).onChange((v) => { music.volume = v })

const world = new CANNON.World()
world.gravity.set(0, - 2.8, 0)
world.broadphase = new CANNON.NaiveBroadphase()
world.solver.iterations = 10
const fixedPhysicsTimeStep = 1 / 60

const curtainSimulations = []
const windForce = new CANNON.Vec3()
const tempWorldPosition = new THREE.Vector3()
const tempLocalPosition = new THREE.Vector3()
const gltfLoader = new GLTFLoader()
let mixer = null
let modelAction = null
let animationPlaying = false
let playStartTime = null
let lastAnimationPlaying = false
const targetKeyBlue = new THREE.Color('rgb(15, 86, 251)')
const keyStartColorA = new THREE.Color()
const keyStartColorB = new THREE.Color()
const autoCamera = {
    enabled: false,
    mode: 'shot1',
    startTime: null
}
const shot1Config = {
    startZ: 12,
    startY: 3.2,
    endOffsetZ: 10,
    endY: 2.6,
    duration: 12
}
const shot2Config = {
    duration: 6,
    offset: new THREE.Vector3(3.4, 3.2, 8)
}
const shot3Config = {
    duration: 4,
    offset: new THREE.Vector3(- 0.6, 2.8, - 3)
}
// const cameraShots = [ 'shot1', 'shot2', 'shot3' ]
// const cameraShots = [ 'shot3' ]


const textureLoader = new THREE.TextureLoader()


/**
 * Floor
 */

const clubFloorMaterial = new THREE.MeshStandardMaterial({
    color: 'rgba(18, 2, 16, 1)',
    roughness: 0.85,
    metalness: 0.05
})

const clubFloor = new THREE.Mesh(
    new THREE.CircleGeometry(30, 64),
    clubFloorMaterial
)
clubFloor.rotation.x = - Math.PI * 0.5
clubFloor.receiveShadow = true
scene.add(clubFloor)

/**
 * Stage
 */
const stageGroup = new THREE.Group()
stageGroup.position.z = - 4
scene.add(stageGroup)

const stageDimensions = {
    radius: 4.2,
    footHeight: 0.18,
    bodyHeight: 0.4,
    deckThickness: 0.1
}
const stageSurfaceY = stageDimensions.footHeight + stageDimensions.bodyHeight + stageDimensions.deckThickness

const footMaterial = new THREE.MeshStandardMaterial({
    color: '#1b1418',
    roughness: 0.75,
    metalness: 0.05
})
const stageMaterial = new THREE.MeshStandardMaterial({
    color: '#342224',
    roughness: 0.65,
    metalness: 0.18
})



//deck

const deckColorMaterial = textureLoader.load('floor/hangar_concrete_floor_diff_1k.jpg')
const deckARMMaterial = textureLoader.load('floor/hangar_concrete_floor_arm_1k.jpg')
const deckDispMaterial = textureLoader.load('floor/hangar_concrete_floor_disp_1k.jpg')
const deckNormalMaterial = textureLoader.load('floor/hangar_concrete_floor_nor_gl_1k.jpg')
;[
    deckColorMaterial,
    deckARMMaterial,
    deckDispMaterial,
    deckNormalMaterial
].forEach((texture) =>
{
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.repeat.set(4, 2)
})



const deckMaterial = new THREE.MeshStandardMaterial({
    map:deckColorMaterial,
    roughness: 0.45,
    metalness: 0.2
})
const trimMaterial = new THREE.MeshStandardMaterial({
    color: '#d2a14c',
    roughness: 0.2,
    metalness: 0.9
})

const stageFoot = new THREE.Mesh(
    new THREE.CylinderGeometry(stageDimensions.radius + 0.3, stageDimensions.radius + 0.3, stageDimensions.footHeight, 64),
    footMaterial
)
stageFoot.position.y = stageDimensions.footHeight * 0.5
stageGroup.add(stageFoot)

const stageBody = new THREE.Mesh(
    new THREE.CylinderGeometry(stageDimensions.radius, stageDimensions.radius, stageDimensions.bodyHeight, 128),
    stageMaterial
)
stageBody.position.y = stageDimensions.footHeight + stageDimensions.bodyHeight * 0.5
stageGroup.add(stageBody)

const stageDeck = new THREE.Mesh(
    new THREE.CylinderGeometry(stageDimensions.radius - 0.2, stageDimensions.radius - 0.2, stageDimensions.deckThickness, 128),
    deckMaterial
)
stageDeck.position.y = stageDimensions.footHeight + stageDimensions.bodyHeight + stageDimensions.deckThickness * 0.5
stageGroup.add(stageDeck)

const stageTrim = new THREE.Mesh(
    new THREE.TorusGeometry(stageDimensions.radius - 0.2, 0.03, 16, 80),
    trimMaterial
)
stageTrim.rotation.x = Math.PI * 0.5
stageTrim.position.y = stageDimensions.footHeight + stageDimensions.bodyHeight + stageDimensions.deckThickness * 0.2
stageGroup.add(stageTrim)

const stageStep = new THREE.Mesh(
    new THREE.BoxGeometry(stageDimensions.radius * 1.35, 0.2, 1.4),
    deckMaterial.clone()
)
stageStep.position.set(0, 0.1, stageDimensions.radius - 0.4)
stageGroup.add(stageStep)

const stageRug = new THREE.Mesh(
    new THREE.CircleGeometry(stageDimensions.radius * 0.65, 48),
    new THREE.MeshStandardMaterial({
        color: '#540b24ff',
        roughness: 0.6,
        metalness: 0.15
    })
)
stageRug.rotation.x = - Math.PI * 0.5
stageRug.position.y = stageSurfaceY + 0.01
stageGroup.add(stageRug)

const stageBackWall = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 6),
    new THREE.MeshStandardMaterial({
        color: '#0c0f1a',
        roughness: 0.9,
        metalness: 0.05
    })
)
stageBackWall.position.set(0, 3, - stageDimensions.radius - 0.4)
stageGroup.add(stageBackWall)

const sideWallMaterial = new THREE.MeshStandardMaterial({
    color: '#1a1924',
    roughness: 0.85,
    metalness: 0.1
})

const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(6, 5), sideWallMaterial)
leftWall.position.set(- stageDimensions.radius - 0.4, 2.6, - stageDimensions.radius * 0.25)
leftWall.rotation.y = Math.PI * 0.45
stageGroup.add(leftWall)

const rightWall = leftWall.clone()
rightWall.position.x *= - 1
rightWall.rotation.y *= - 1
stageGroup.add(rightWall)

const columnMaterial = new THREE.MeshStandardMaterial({
    color: '#1e1d27',
    roughness: 0.6,
    metalness: 0.2
})

const leftColumn = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.1, 4.6, 16),
    columnMaterial
)
leftColumn.position.set(- stageDimensions.radius + 0.4, 2.4, stageDimensions.radius - 0.5)
stageGroup.add(leftColumn)

const rightColumn = leftColumn.clone()
rightColumn.position.x *= - 1
stageGroup.add(rightColumn)

const topBeam = new THREE.Mesh(new THREE.BoxGeometry(9, 0.15, 0.4), columnMaterial)
topBeam.position.set(0, 4.6, stageDimensions.radius - 0.5)
stageGroup.add(topBeam)

/**
 * Curtains
 */

const curtainNormalMaterial = textureLoader.load("./curtain/velour_velvet_nor_gl_1k.jpg")
const curtainDisplacementMaterial = textureLoader.load("./curtain/velour_velvet_disp_1k.jpg")
const curtainARMMaterial = textureLoader.load("./curtain/velour_velvet_arm_1k.jpg")
const curtainDiffMaterial = textureLoader.load("./curtain/velour_velvet_diff_1k.jpg")
curtainDiffMaterial.colorSpace = THREE.SRGBColorSpace

const tileX = 4
const tileY = 1.5

;[
    curtainDiffMaterial,
    curtainARMMaterial,
    curtainNormalMaterial,
    curtainDisplacementMaterial
].forEach((texture) =>
{
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.repeat.set(tileX, tileY)
})



const curtainMaterial = new THREE.MeshStandardMaterial({
    map:curtainDiffMaterial,
    aoMap:curtainARMMaterial,
    roughnessMap:curtainARMMaterial,
    metalnessMap:curtainARMMaterial,
    normalMap:curtainNormalMaterial,
    displacementMap:curtainDisplacementMaterial,
    displacementScale:0.02,
    displacementBias:0.2,
    roughness:0.82,
    metalness:0.05,
    side: THREE.DoubleSide
})

const createCurtainGeometry = (width, height, config = {}) =>
{
    const {
        foldFrequency = 5,
        foldAmplitude = 0.12,
        sagAmplitude = 0.35,
        taper = 0.6,
        widthSegments = 32,
        heightSegments = 32
    } = config

    const geometry = new THREE.PlaneGeometry(width, height, widthSegments, heightSegments)
    geometry.setAttribute('uv2', geometry.attributes.uv.clone())
    const position = geometry.attributes.position

    for(let i = 0; i < position.count; i++)
    {
        const x = position.getX(i)
        const y = position.getY(i)
        const normalizedX = (x + width / 2) / width
        const fold = Math.sin(normalizedX * Math.PI * foldFrequency) * foldAmplitude
        const sag = Math.pow(Math.sin(normalizedX * Math.PI), 1.5) * sagAmplitude
        const verticalFactor = (y + height / 2) / height

        position.setZ(i, fold)
        position.setY(i, y - sag * (1 - verticalFactor * taper))
    }

    geometry.computeVertexNormals()

    return geometry
}

const registerCurtainSimulation = (mesh, {
    pinnedRows = 2,
    mass = 0.35,
    damping = 0.2,
    structuralForce = 45,
    shearForce = 28,
    bendForce = 18,
    windStrength = 0.75
} = {}) =>
{
    const geometry = mesh.geometry
    const widthSegments = geometry.parameters.widthSegments ?? 1
    const heightSegments = geometry.parameters.heightSegments ?? 1
    const columns = widthSegments + 1
    const rows = heightSegments + 1
    geometry.attributes.position.setUsage(THREE.DynamicDrawUsage)
    mesh.updateWorldMatrix(true, false)
    const matrixWorld = mesh.matrixWorld.clone()
    const inverseMatrix = new THREE.Matrix4().copy(matrixWorld).invert()
    const positionAttr = geometry.attributes.position
    const localPosition = new THREE.Vector3()
    const worldPosition = new THREE.Vector3()
    const bodies = new Array(columns * rows)
    const anchorPositions = new Array(columns * rows)

    for(let row = 0; row < rows; row++)
    {
        for(let col = 0; col < columns; col++)
        {
            const index = col + row * columns
            localPosition.set(
                positionAttr.getX(index),
                positionAttr.getY(index),
                positionAttr.getZ(index)
            )
            worldPosition.copy(localPosition).applyMatrix4(matrixWorld)

            // row 0 在 PlaneGeometry 是最上排，这里钉住顶端几排
            const isPinned = row < pinnedRows
            const body = new CANNON.Body({
                mass: isPinned ? 0 : mass,
                position: new CANNON.Vec3(worldPosition.x, worldPosition.y, worldPosition.z),
                linearDamping: damping,
                angularDamping: damping
            })
            if(isPinned)
            {
                body.type = CANNON.Body.STATIC
            }
            body.addShape(new CANNON.Particle())
            world.addBody(body)
            bodies[index] = body
            anchorPositions[index] = isPinned ? worldPosition.clone() : null
        }
    }

    const connect = (indexA, indexB, maxForce) =>
    {
        const bodyA = bodies[indexA]
        const bodyB = bodies[indexB]
        if(!bodyA || !bodyB) return
        const distance = bodyA.position.distanceTo(bodyB.position)
        const constraint = new CANNON.DistanceConstraint(bodyA, bodyB, distance, maxForce)
        world.addConstraint(constraint)
    }

    for(let row = 0; row < rows; row++)
    {
        for(let col = 0; col < columns; col++)
        {
            const index = col + row * columns
            if(col < columns - 1) connect(index, index + 1, structuralForce)
            if(row < rows - 1) connect(index, index + columns, structuralForce)
            if(col < columns - 1 && row < rows - 1)
            {
                connect(index, index + columns + 1, shearForce)
                connect(index + 1, index + columns, shearForce)
            }
            if(col < columns - 2) connect(index, index + 2, bendForce)
            if(row < rows - 2) connect(index, index + columns * 2, bendForce)
        }
    }

    curtainSimulations.push({
        mesh,
        geometry,
        positionAttr,
        bodies,
        columns,
        rows,
        inverseMatrix,
        windStrength,
        windPhase: Math.random() * Math.PI * 2,
        anchorPositions
    })
}

const enforceCurtainAnchors = () =>
{
    curtainSimulations.forEach((simulation) =>
    {
        const { bodies, anchorPositions } = simulation
        for(let i = 0; i < anchorPositions.length; i++)
        {
            const anchor = anchorPositions[i]
            if(!anchor) continue
            const body = bodies[i]
            if(!body) continue
            body.position.set(anchor.x, anchor.y, anchor.z)
            body.velocity.set(0, 0, 0)
            body.angularVelocity.set(0, 0, 0)
            body.force.set(0, 0, 0)
            body.torque.set(0, 0, 0)
        }
    })
}

const applyCurtainWind = (elapsedTime) =>
{
    if(curtainSimulations.length === 0) return

    curtainSimulations.forEach((simulation) =>
    {
        const { bodies, columns, rows, windStrength, windPhase } = simulation
        for(let row = 0; row < rows; row++)
        {
            const heightFactor = row / rows
            for(let col = 0; col < columns; col++)
            {
                const index = col + row * columns
                const body = bodies[index]
                if(!body || body.mass === 0) continue

                const sway = Math.sin(elapsedTime * 0.35 + windPhase + col * 0.12)
                const lift = Math.cos(elapsedTime * 0.45 + row * 0.15 + windPhase * 0.5)

                windForce.set(
                    0.4 * sway,
                    0.05 * lift,
                    0.15 * Math.cos(elapsedTime * 0.25 + windPhase)
                )
                const intensity = heightFactor * windStrength
                windForce.x *= intensity
                windForce.y *= intensity
                windForce.z *= intensity

                body.applyForce(windForce, body.position)
            }
        }
    })
}

const updateCurtainMeshes = () =>
{
    if(curtainSimulations.length === 0) return

    curtainSimulations.forEach((simulation) =>
    {
        const { bodies, positionAttr, inverseMatrix, geometry } = simulation
        for(let i = 0; i < bodies.length; i++)
        {
            const body = bodies[i]
            if(!body) continue
            tempWorldPosition.set(body.position.x, body.position.y, body.position.z)
            tempLocalPosition.copy(tempWorldPosition).applyMatrix4(inverseMatrix)
            positionAttr.setXYZ(i, tempLocalPosition.x, tempLocalPosition.y, tempLocalPosition.z)
        }
        positionAttr.needsUpdate = true
        geometry.computeVertexNormals()
        geometry.attributes.normal.needsUpdate = true
    })
}

const updateKeyLightColors = (elapsedTime) =>
{
    if(!animationPlaying || playStartTime === null) return

    const fadeStart = 13
    const fadeEnd = 16.5
    const elapsedSincePlay = elapsedTime - playStartTime

    if(elapsedSincePlay < fadeStart)
    {
        sideKeyLight.color.copy(keyStartColorA)
        sideKeyBLight.color.copy(keyStartColorB)
        return
    }

    if(elapsedSincePlay < fadeEnd)
    {
        const t = (elapsedSincePlay - fadeStart) / (fadeEnd - fadeStart)
        sideKeyLight.color.copy(keyStartColorA).lerp(targetKeyBlue, t)
        sideKeyBLight.color.copy(keyStartColorB).lerp(targetKeyBlue, t)
        return
    }

    sideKeyLight.color.copy(targetKeyBlue)
    sideKeyBLight.color.copy(targetKeyBlue)
}

const updateCameraShot = (elapsedTime) =>
{
    if(!autoCamera.enabled) return
    if(autoCamera.startTime === null) autoCamera.startTime = elapsedTime

    const focusX = 0
    const focusY = 1.6
    const focusZ = stageGroup.position.z

    const totalDuration = shot1Config.duration + shot2Config.duration + shot3Config.duration
    const localTime = (elapsedTime - autoCamera.startTime) % totalDuration

    if(localTime < shot1Config.duration)
    {
        const startPos = new THREE.Vector3(0, shot1Config.startY, shot1Config.startZ)
        const endPos = new THREE.Vector3(0, shot1Config.endY, focusZ + shot1Config.endOffsetZ)

        const tNorm = localTime / shot1Config.duration
        const progress = 0.5 - 0.5 * Math.cos(tNorm * Math.PI * 2) // ping-pong ease

        camera.position.copy(startPos).lerp(endPos, progress)
        camera.lookAt(focusX, focusY, focusZ)
    }
    else if(localTime < shot1Config.duration + shot2Config.duration)
    {
        const shot2Pos = new THREE.Vector3(
            shot2Config.offset.x,
            shot2Config.offset.y,
            focusZ + shot2Config.offset.z
        )
        camera.position.copy(shot2Pos)
        camera.lookAt(focusX, focusY, focusZ)
    }
    else
    {
        const shot3Pos = new THREE.Vector3(
            shot3Config.offset.x,
            shot3Config.offset.y,
            focusZ + shot3Config.offset.z
        )
        camera.position.copy(shot3Pos)
        camera.lookAt(focusX, focusY, focusZ)
    }

    camera.updateMatrixWorld()
   
}

const leftCurtain = new THREE.Mesh(
    createCurtainGeometry(3.8, 4.3, { foldFrequency: 7, foldAmplitude: 0.16, sagAmplitude: 0.75, widthSegments: 30, heightSegments: 36 }),
    curtainMaterial
)
leftCurtain.position.set(- stageDimensions.radius + 0.35, 2.6, stageDimensions.radius - 0.35)
leftCurtain.rotation.y = 0.35
stageGroup.add(leftCurtain)

const rightCurtain = new THREE.Mesh(
    createCurtainGeometry(3.8, 4.3, { foldFrequency: 7, foldAmplitude: 0.16, sagAmplitude: 0.75, widthSegments: 30, heightSegments: 36 }),
    curtainMaterial
)
rightCurtain.position.set(stageDimensions.radius - 0.35, 2.6, stageDimensions.radius - 0.35)
rightCurtain.rotation.y = - 0.35
stageGroup.add(rightCurtain)

scene.updateMatrixWorld(true)
registerCurtainSimulation(leftCurtain, { pinnedRows: 2, windStrength: 0.9 })
registerCurtainSimulation(rightCurtain, { pinnedRows: 2, windStrength: 0.9 })

gltfLoader.load(
    '/model/final.glb',
    (gltf) =>
    {
        const model = gltf.scene
        model.position.set(0, stageSurfaceY, 3)
        model.scale.setScalar(1)
        model.traverse((child) =>
        {
            if(child.isMesh)
            {
                child.castShadow = true
                child.receiveShadow = true
            }
        })
        stageGroup.add(model)

        if(gltf.animations && gltf.animations.length > 0)
        {
            mixer = new THREE.AnimationMixer(model)
            modelAction = mixer.clipAction(gltf.animations[0])
            modelAction.setLoop(THREE.LoopPingPong, Infinity)
            modelAction.clampWhenFinished = false
            modelAction.paused = true
            modelAction.play()
        }
    },
    undefined,
    (error) =>
    {
        console.error('Failed to load /model/final.glb', error)
    }
)

const topValance = new THREE.Mesh(
    createCurtainGeometry(9.4, 1.4, { foldFrequency: 9, foldAmplitude: 0.09, sagAmplitude: 0.35, taper: 0.2 }),
    curtainMaterial.clone()
)
topValance.position.set(0, 4.2, stageDimensions.radius - 0.4)
stageGroup.add(topValance)

const backdropDrape = new THREE.Mesh(
    createCurtainGeometry(10, 4.8, { foldFrequency: 4, foldAmplitude: 0.08, sagAmplitude: 0.28, taper: 0.4 }),
    new THREE.MeshStandardMaterial({
        color: '#1a0d17',
        roughness: 0.85,
        metalness: 0.1,
        side: THREE.DoubleSide
    })
)
backdropDrape.position.set(0, 2.8, - stageDimensions.radius + 0.2)
backdropDrape.rotation.y = Math.PI
stageGroup.add(backdropDrape)

const curtainRod = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.05, 9, 16),
    trimMaterial
)
curtainRod.position.set(0, 4.4, stageDimensions.radius - 0.55)
curtainRod.rotation.z = Math.PI * 0.5
stageGroup.add(curtainRod)

const tieMaterial = trimMaterial.clone()
const leftTie = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.035, 12, 32), tieMaterial)
leftTie.position.set(- stageDimensions.radius + 0.4, 2.15, stageDimensions.radius - 0.25)
leftTie.rotation.y = Math.PI * 0.5
stageGroup.add(leftTie)

const rightTie = leftTie.clone()
rightTie.position.x *= - 1
stageGroup.add(rightTie)

const stageMeshes = [
    stageFoot,
    stageBody,
    stageDeck,
    stageTrim,
    stageStep,
    stageRug,
    stageBackWall,
    leftWall,
    rightWall,
    leftColumn,
    rightColumn,
    topBeam,
    leftCurtain,
    rightCurtain,
    topValance,
    backdropDrape,
    curtainRod,
    leftTie,
    rightTie
]
stageMeshes.forEach((mesh) =>
{
    mesh.castShadow = true
    mesh.receiveShadow = true
})

/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight('#432b30', 2)
scene.add(ambientLight)

const hemiLight = new THREE.HemisphereLight('#f1d7b3', '#050505',2)
scene.add(hemiLight)
const lightFolder = gui.addFolder('Lights')
lightFolder.add(ambientLight, 'intensity').min(0).max(20).step(0.01).name('ambientIntensity')
lightFolder.add(hemiLight, 'intensity').min(0).max(20).step(0.01).name('hemiIntensity')

const stageFocusY = stageSurfaceY + 0.5
const stageFocusZ = stageGroup.position.z - 0.8

const spotLightLeft = new THREE.SpotLight('#2160cdff', 30, 30, Math.PI * 0.3, 0.35)
spotLightLeft.position.set(- 4.5, 7, 3.2)
spotLightLeft.castShadow = true
spotLightLeft.shadow.mapSize.set(1024, 1024)
scene.add(spotLightLeft)

const leftTarget = new THREE.Object3D()
leftTarget.position.set(- 1.1, stageFocusY, stageFocusZ)
scene.add(leftTarget)
spotLightLeft.target = leftTarget
lightFolder.add(spotLightLeft, 'intensity').min(0).max(60).step(0.1).name('spotLIntensity')

const spotLightRight = new THREE.SpotLight('#ffdcb0', 14, 30, Math.PI * 0.3, 0.35)
spotLightRight.position.set(4.5, 7, 3.2)
spotLightRight.castShadow = true
spotLightRight.shadow.mapSize.set(1024, 1024)
scene.add(spotLightRight)

const rightTarget = new THREE.Object3D()
rightTarget.position.set(1.1, stageFocusY, stageFocusZ)
scene.add(rightTarget)
spotLightRight.target = rightTarget
lightFolder.add(spotLightRight, 'intensity').min(0).max(60).step(0.1).name('spotRIntensity')

const centerSpot = new THREE.SpotLight('#fff1d6', 10, 25, Math.PI * 0.28, 40)
centerSpot.position.set(0, 7.5, 1.5)
centerSpot.castShadow = true
centerSpot.shadow.mapSize.set(1024, 1024)
scene.add(centerSpot)

const centerTarget = new THREE.Object3D()
centerTarget.position.set(0, stageFocusY, stageGroup.position.z - 0.2)
scene.add(centerTarget)
centerSpot.target = centerTarget
lightFolder.add(centerSpot, 'intensity').min(0).max(100).step(0.1).name('centerIntensity')

const sideKeyTarget = new THREE.Object3D()
sideKeyTarget.position.set(0, stageSurfaceY + 1, stageGroup.position.z)
scene.add(sideKeyTarget)






//KeyLight A
const keyLightAColor = 'rgba(255, 255, 255, 1)'
const sideKeyLight = new THREE.SpotLight(keyLightAColor, 30, 20, Math.PI * 0.3, 0.35)
sideKeyLight.position.set(-2.4, 3.6, 0.8)
sideKeyLight.castShadow = true
sideKeyLight.shadow.mapSize.set(1024, 1024)
sideKeyLight.shadow.camera.fov = THREE.MathUtils.radToDeg(sideKeyLight.angle * 2)
sideKeyLight.shadow.camera.updateProjectionMatrix()
sideKeyLight.target = sideKeyTarget
scene.add(sideKeyLight)
const sideKeyCameraHelper = new THREE.CameraHelper(sideKeyLight.shadow.camera)
sideKeyCameraHelper.visible = false
scene.add(sideKeyCameraHelper)
const sideKeyFolder = gui.addFolder('Side Key Light')
const sideKeyParams = { color: keyLightAColor}
sideKeyFolder.addColor(sideKeyParams, 'color').onChange((value) => sideKeyLight.color.set(value))
sideKeyFolder.add(sideKeyLight, 'intensity').min(0).max(30).step(0.1)
sideKeyFolder.add(sideKeyLight, 'angle').min(0.1).max(Math.PI * 0.6).step(0.01).onChange(() =>
{
    sideKeyLight.shadow.camera.fov = THREE.MathUtils.radToDeg(sideKeyLight.angle * 2)
    sideKeyLight.shadow.camera.updateProjectionMatrix()
    sideKeyCameraHelper.update()
})
sideKeyFolder.add(sideKeyLight, 'penumbra').min(0).max(1).step(0.01)
sideKeyFolder.add(sideKeyLight.position, 'x').min(- 8).max(8).step(0.1)
sideKeyFolder.add(sideKeyLight.position, 'y').min(0).max(10).step(0.1)
sideKeyFolder.add(sideKeyLight.position, 'z').min(- 6).max(6).step(0.1)
sideKeyFolder.add(sideKeyCameraHelper, 'visible').name('shadow cam helper')







//KeyLight B
const keyLightBColor = keyLightAColor
const sideKeyBLight = new THREE.SpotLight(keyLightBColor, 14, 20, Math.PI * 0.3, 0.35)
sideKeyBLight.position.set(2.4, 3.6, 0.8)
sideKeyBLight.castShadow = true
sideKeyBLight.shadow.mapSize.set(1024, 1024)
sideKeyBLight.shadow.camera.fov = THREE.MathUtils.radToDeg(sideKeyBLight.angle * 2)
sideKeyBLight.shadow.camera.updateProjectionMatrix()
sideKeyBLight.target = sideKeyTarget
scene.add(sideKeyBLight)
const sideKeyBCameraHelper = new THREE.CameraHelper(sideKeyBLight.shadow.camera)
sideKeyBCameraHelper.visible = false
scene.add(sideKeyBCameraHelper)
const sideKeyBFolder = gui.addFolder('Side Key B Light')
const sideKeyBParams = { color: keyLightBColor}
sideKeyBFolder.addColor(sideKeyBParams, 'color').onChange((value) => sideKeyBLight.color.set(value))
sideKeyBFolder.add(sideKeyBLight, 'intensity').min(0).max(30).step(0.1)
sideKeyBFolder.add(sideKeyBLight, 'angle').min(0.1).max(Math.PI * 0.6).step(0.01).onChange(() =>
{
    sideKeyBLight.shadow.camera.fov = THREE.MathUtils.radToDeg(sideKeyBLight.angle * 2)
    sideKeyBLight.shadow.camera.updateProjectionMatrix()
    sideKeyBCameraHelper.update()
})
sideKeyBFolder.add(sideKeyBLight, 'penumbra').min(0).max(1).step(0.01)
sideKeyBFolder.add(sideKeyBLight.position, 'x').min(- 8).max(8).step(0.1)
sideKeyBFolder.add(sideKeyBLight.position, 'y').min(0).max(10).step(0.1)
sideKeyBFolder.add(sideKeyBLight.position, 'z').min(- 6).max(6).step(0.1)
sideKeyBFolder.add(sideKeyBCameraHelper, 'visible').name('shadow cam helper')

keyStartColorA.copy(sideKeyLight.color)
keyStartColorB.copy(sideKeyBLight.color)




const rimLight = new THREE.PointLight('#3a6cff', 3, 12)
rimLight.position.set(0, 3.2, stageGroup.position.z - stageDimensions.radius - 0.5)
scene.add(rimLight)

const footLights = new THREE.PointLight('#ff5e3a', 2.5, 8)
footLights.position.set(0, 0.4, stageGroup.position.z + stageDimensions.radius - 0.4)
footLights.castShadow = true
stageGroup.add(footLights)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    effectComposer.setSize(sizes.width, sizes.height)
    effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    if(letterboxVisible)
    {
        setLetterbox(true)
    }
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(70, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 3.2, 10)
scene.add(camera)


const cameraParams = { cinematic: false }
gui.add(cameraParams, 'cinematic').name('Camera Cinematic').onChange((value) =>
{
    autoCamera.enabled = value
    controls.enabled = !value
    autoCamera.startTime = null
})

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.target.set(0, 1.6, stageGroup.position.z)
controls.update()

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor('#030306')
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.outputColorSpace = THREE.SRGBColorSpace

const effectComposer = new EffectComposer(renderer)
effectComposer.setSize(sizes.width, sizes.height)
effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
const renderPass = new RenderPass(scene, camera)
effectComposer.addPass(renderPass)

const GrainShader = {
    uniforms:
    {
        tDiffuse: { value: null },
        uTime: { value: 0 },
        uAmount: { value: 0.04 }
    },
    vertexShader: `
        varying vec2 vUv;
        void main()
        {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uTime;
        uniform float uAmount;
        varying vec2 vUv;

        // Simple pseudo-random
        float random(vec2 co)
        {
            return fract(sin(dot(co.xy ,vec2(12.9898,78.233)) + uTime * 1.318) * 43758.5453);
        }

        void main()
        {
            vec4 baseColor = texture2D(tDiffuse, vUv);
            float grain = random(vUv);
            baseColor.rgb += (grain - 0.5) * uAmount;
            gl_FragColor = baseColor;
        }
    `
}
const grainPass = new ShaderPass(GrainShader)
effectComposer.addPass(grainPass)
gui.add(grainPass.material.uniforms.uAmount, 'value').min(0).max(0.2).step(0.001).name('grain amount')

/**
 * Animate
 */
const timer = new Timer()
const animatedCurtainPieces = [topValance]

const tick = () =>
{
    timer.update()
    const deltaTime = timer.getDelta()
    const elapsedTime = timer.getElapsed()

    if(animationPlaying && !lastAnimationPlaying)
    {
        playStartTime = elapsedTime
        keyStartColorA.copy(sideKeyLight.color)
        keyStartColorB.copy(sideKeyBLight.color)
    }
    lastAnimationPlaying = animationPlaying

    applyCurtainWind(elapsedTime)
    enforceCurtainAnchors()
    world.step(fixedPhysicsTimeStep, deltaTime, 3)
    updateCurtainMeshes()
    if(mixer && animationPlaying)
    {
        mixer.update(deltaTime)
    }
    updateKeyLightColors(elapsedTime)
    grainPass.material.uniforms.uTime.value = elapsedTime

    const sway = Math.sin(elapsedTime * 0.4) * 0.4
    leftTarget.position.x = - 1.1 + sway * 0.15
    rightTarget.position.x = 1.1 - sway * 0.15
    centerTarget.position.y = stageFocusY + Math.sin(elapsedTime * 0.5) * 0.15

    animatedCurtainPieces.forEach((mesh, index) =>
    {
        mesh.rotation.z = Math.sin(elapsedTime * 0.15 + index) * 0.03
    })

    if(autoCamera.enabled)
    {
        controls.enabled = false
        updateCameraShot(elapsedTime)
    }
    else
    {
        controls.update()
    }

    effectComposer.render()
    window.requestAnimationFrame(tick)
}

tick()
